import { motion } from "framer-motion";
import { Target, Crosshair, Users, Film, ArrowUpRight } from "lucide-react";
import { PROGRAMS } from "@/constants/testIds";

const programs = [
  {
    id: PROGRAMS.cardSkill,
    icon: Target,
    title: "Skill Development",
    desc: "Build a complete, game-ready skillset. Footwork, ball handling, finishing — sharpened with intention.",
    image: "https://images.unsplash.com/photo-1546519638-68e109498ffc",
    span: "lg:col-span-7 lg:row-span-2",
  },
  {
    id: PROGRAMS.cardShooting,
    icon: Crosshair,
    title: "Shooting Camps",
    desc: "Form, range, and confidence. Become a threat from every level on the floor.",
    image: "https://images.unsplash.com/photo-1629901925121-8a141c2a42f4",
    span: "lg:col-span-5",
  },
  {
    id: PROGRAMS.cardPosition,
    icon: Users,
    title: "Position Camps",
    desc: "Guard, wing, and big-specific training. Master your role, then expand it.",
    image: "https://images.pexels.com/photos/32619170/pexels-photo-32619170.jpeg",
    span: "lg:col-span-5",
  },
  {
    id: PROGRAMS.cardFilm,
    icon: Film,
    title: "Private Film Study",
    desc: "For players serious about the game. We break down your tape — possession by possession.",
    image: "https://images.unsplash.com/photo-1520399636535-24741e71b160",
    span: "lg:col-span-7",
  },
];

export default function Programs() {
  return (
    <section
      id="programs"
      data-testid={PROGRAMS.section}
      className="section-pad bg-[#03080F] relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="overline">What We Offer</div>
            <div className="gold-divider mt-4 mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Programs engineered for{" "}
              <span className="text-[#B8C5D6]">the next level.</span>
            </h2>
          </div>
          <p className="text-[#A0ABC0] max-w-md text-base">
            Every camp, every session — designed to translate from the gym to
            the game. Train with purpose.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 auto-rows-[minmax(260px,auto)]">
          {programs.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.a
                key={p.id}
                href="#contact"
                data-testid={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`bba-card relative overflow-hidden p-7 sm:p-9 group ${p.span}`}
              >
                <div
                  className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                  style={{
                    backgroundImage: `url(${p.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/85 to-[#0A1128]/45" />
                <div className="relative flex flex-col h-full min-h-[240px]">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 grid place-items-center border border-[#B8C5D6]/40 text-[#B8C5D6]">
                      <Icon size={20} />
                    </div>
                    <ArrowUpRight
                      size={20}
                      className="text-white/40 group-hover:text-[#B8C5D6] group-hover:rotate-12 transition-all"
                    />
                  </div>
                  <div className="mt-auto pt-10">
                    <h3 className="font-display text-2xl sm:text-3xl tracking-tight">
                      {p.title}
                    </h3>
                    <p className="text-[#A0ABC0] mt-3 text-sm sm:text-base max-w-md">
                      {p.desc}
                    </p>
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
